import {
  endOfMonth,
  endOfYear,
  format,
  isValid,
  parse,
  startOfMonth,
  startOfYear,
  subDays,
  subMonths,
} from 'date-fns'
import type { PresetRange } from '@/components/ui/AdvancedDateRangePicker'
import type { ResidentDateRange, ResidentDateRangePreset } from '../types'

const API_DATE_FORMAT = 'dd-MM-yyyy'
const DISPLAY_DATE_FORMAT = 'MM-dd-yyyy'

/**
 * Angular DatePipe parity: a date-only `YYYY-MM-DD` is a local calendar date, not UTC
 * midnight (which renders a day early west of UTC). Numbers are epoch seconds.
 */
export function parseDisplayDate(value: unknown): Date | null {
  if (value === null || value === undefined || value === '') return null
  if (typeof value === 'number') {
    const fromEpoch = new Date(value * 1000)
    return Number.isNaN(fromEpoch.getTime()) ? null : fromEpoch
  }
  const raw = String(value).trim()
  if (!raw) return null
  const date = new Date(/^\d{4}-\d{2}-\d{2}$/.test(raw) ? `${raw}T12:00:00` : raw)
  return Number.isNaN(date.getTime()) ? null : date
}

export function formatDateForApi(
  value: Date | string | null | undefined,
): string {
  if (!value) return ''
  const date = value instanceof Date ? value : new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  return format(date, API_DATE_FORMAT)
}

export function formatDateForDisplay(
  value: Date | string | null | undefined,
): string {
  if (!value) return ''
  const date = value instanceof Date ? value : new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  return format(date, DISPLAY_DATE_FORMAT)
}

/** Parse API date strings for resident list filters (`dd-MM-yyyy`; tolerate `yyyy-MM-dd` / ISO). */
export function parseResidentApiDate(value?: string | null): Date | null {
  if (value == null || String(value).trim() === '') return null
  const s = String(value).trim()
  const dmy = parse(s, API_DATE_FORMAT, new Date())
  if (isValid(dmy)) return dmy
  const ymd = parse(s, 'yyyy-MM-dd', new Date())
  if (isValid(ymd)) return ymd
  const fallback = new Date(s)
  return Number.isNaN(fallback.getTime()) ? null : fallback
}

/**
 * Same presets as `createResidentDateRanges` / Angular resident list — for `AdvancedDateRangePicker`.
 */
export function getResidentLeaseStartPresetRanges(
  now = new Date(),
): PresetRange[] {
  return createResidentDateRanges(now).map((preset) => ({
    label: preset.label.trim(),
    getValue: () => {
      const fromStr = preset.value.lease_start_fromDt
      const toStr = preset.value.lease_start_toDt
      const from = parse(fromStr!, API_DATE_FORMAT, new Date())
      const to = parse(toStr!, API_DATE_FORMAT, new Date())
      return [from, to]
    },
  }))
}

export function createResidentDateRanges(
  now = new Date(),
): ResidentDateRangePreset[] {
  const currentYear = now.getFullYear()
  const preset = (
    label: string,
    from: Date,
    to: Date,
  ): ResidentDateRangePreset => ({
    label,
    value: {
      lease_start_fromDt: formatDateForApi(from),
      lease_start_toDt: formatDateForApi(to),
    },
  })

  const ranges: ResidentDateRangePreset[] = [
    preset('Today', now, now),
    preset('Yesterday', subDays(now, 1), subDays(now, 1)),
    preset('Last 7 Days', subDays(now, 6), now),
    preset('Last 30 Days', subDays(now, 29), now),
    preset('This Month', startOfMonth(now), endOfMonth(now)),
    preset(
      'Last Month',
      startOfMonth(subMonths(now, 1)),
      endOfMonth(subMonths(now, 1)),
    ),
  ]

  for (let i = 0; i < 3; i += 1) {
    const yearDate = new Date(currentYear - i, 0, 1)
    ranges.push(
      preset(` ${currentYear - i}`, startOfYear(yearDate), endOfYear(yearDate)),
    )
  }

  return ranges
}

export function formatApiRangeLabel(
  range?: ResidentDateRange | string,
): string {
  if (!range || typeof range === 'string')
    return typeof range === 'string' ? range : ''
  const from = range.lease_start_fromDt
  const to = range.lease_start_toDt
  if (!from || !to) return ''
  return `${from} - ${to}`
}

/**
 * Safely parse date strings (e.g. 'YYYY-MM-DD', 'YYYY-MM-DDTHH:mm:ss') as LOCAL dates,
 * avoiding UTC-midnight date rollback in negative timezones.
 */
export function parseLocalDate(value?: string | Date | null): Date | null {
  if (!value) return null
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value
  const str = String(value).trim()
  if (!str) return null
  const ymdMatch = str.match(/^(\d{4})-(\d{2})-(\d{2})/)
  if (ymdMatch) {
    const [, y, m, d] = ymdMatch
    return new Date(Number(y), Number(m) - 1, Number(d))
  }
  const fallback = new Date(str)
  return Number.isNaN(fallback.getTime()) ? null : fallback
}

export function formatResidentListDate(
  value?: string | Date | null,
  mode: 'date' | 'year' = 'date',
): string {
  const date = parseLocalDate(value)
  if (!date) return ''
  if (mode === 'year') return String(date.getFullYear())
  return date.toLocaleDateString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
  })
}
